<template>
    <div class="services-tab">
        <div class="row mb-2" v-if="validationErrors.apiError">
            <div class="col">
                <el-alert type="error">
                    {{ validationErrors.apiError }}
                </el-alert>
            </div>
        </div>

        <!-- Suppliers Section -->
        <div class="row mt-4">
            <div class="col-12">
                <base-input label="What suppliers do you require?">
                    <div class="mb-2 text-muted small">Please tick all that apply</div>
                    <div class="row">
                        <div class="col-md-6">
                            <el-checkbox-group v-model="form.supplier_preference">
                                <div v-for="option in supplierOptions.slice(0, Math.ceil(supplierOptions.length / 2))"
                                    :key="option.value" class="mb-2">
                                    <el-checkbox :label="option.value" :key="option.value">
                                        {{ option.label }}
                                    </el-checkbox>
                                </div>
                            </el-checkbox-group>
                        </div>
                        <div class="col-md-6">
                            <el-checkbox-group v-model="form.supplier_preference">
                                <div v-for="option in supplierOptions.slice(Math.ceil(supplierOptions.length / 2))"
                                    :key="option.value" class="mb-2">
                                    <el-checkbox :label="option.value" :key="option.value">
                                        {{ option.label }}
                                    </el-checkbox>
                                </div>
                            </el-checkbox-group>
                        </div>
                    </div>
                </base-input>
            </div>
        </div>

        <!-- Accommodation Section -->
        <div class="row mt-4">
            <div class="col-12">
                <base-input label="What Event Management Services do you require?">
                    <el-radio-group v-model="form.event_management_service">
                        <el-radio v-for="(service, index) in offeredEventManagementServices" :key="`${index}-${service.value}`" :label="service.value">
                            {{ service.label }}
                            <span v-if="service.note" class="text-muted small">{{ service.note }}</span>
                        </el-radio>
                    </el-radio-group>
                </base-input>
            </div>
        </div>

        <!-- Save Button -->
        <div class="row mt-4">
            <div class="col-12 text-right">
                <base-button type="primary" @click="saveServices" :loading="loading">
                    Save & Next
                </base-button>
            </div>
        </div>
    </div>
</template>

<script>
import {
    Notification,
    Checkbox,
    CheckboxGroup,
    Radio,
    RadioGroup,
    Alert
} from "element-ui";
import LeadEventService from '@/services/lead-event.service';

export default {
    name: 'EditEventStep4',
    components: {
        [Notification.name]: Notification,
        [Checkbox.name]: Checkbox,
        [CheckboxGroup.name]: CheckboxGroup,
        [Radio.name]: Radio,
        [RadioGroup.name]: RadioGroup,
        [Alert.name]: Alert
    },
    props: {
        leadEvent: {
            type: Object,
            required: true,
        },
        sessionId: {
            type: [Number, String],
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            validationErrors: {},
            form: {
                step: 4,
                direct_update: true,
                supplier_preference: [],
                event_management_service: null,
            },
            supplierOptions: [],
            offeredEventManagementServices: [
                {
                    label: "Venue Sourcing, Supplier booking and Accommodation only",
                    value: 'venue_sourcing_supplier_booking_accommodation'
                },
                {
                    label: "Event Coordination including on the day support",
                    value: 'event_coordination',
                    note: "(Additional costs apply)"
                },
                {
                    label: "Full Service Event Management",
                    value: 'full_service_event_management',
                    note: "(Additional costs apply)"
                },
            ],
        };
    },
    methods: {
        async saveServices() {
            this.validationErrors = {};
            this.$emit('update:loading', true);
            try {
                if (this.sessionId) {
                    await LeadEventService.updateEventSession(this.leadEvent.id, {
                        ...this.form,
                        session_id: this.sessionId,
                    });
                } else {
                    await LeadEventService.updateCorporateLeadEvent(this.leadEvent.id, this.form);
                }

                this.$emit('onSave', {
                    message: 'Services saved successfully',
                });

                Notification.success({
                    title: 'Success',
                    message: 'Services saved successfully!',
                    type: 'success',
                });
            } catch (error) {
                console.error('Error saving services:', error);
                const responseData = error?.response?.data || {};
                const message = responseData.error?.message
                    || responseData.message
                    || 'Failed to save services. Please check your inputs and try again.';

                if (responseData.errors && typeof responseData.errors === 'object') {
                    Object.keys(responseData.errors).forEach((field) => {
                        const firstError = Array.isArray(responseData.errors[field])
                            ? responseData.errors[field][0]
                            : responseData.errors[field];

                        this.$set(this.validationErrors, field, firstError);
                    });
                }

                this.$set(this.validationErrors, 'apiError', message);

                Notification.error({
                    title: 'Error',
                    message,
                    type: 'error',
                    duration: 5000
                });
                this.$emit('update:loading', false);
            }
        },

        updateFormFromLeadEvent(leadEvent) {
            this.supplierOptions = leadEvent?.options?.offered_supplier_types || [];

            let dataToUse = leadEvent;
            if (this.sessionId) {
                const sessions = leadEvent.event_sessions || leadEvent.eventSessions || [];
                const session = sessions.find(s => s.id === this.sessionId);
                if (session) {
                    dataToUse = {
                        ...leadEvent,
                        ...session
                    };
                }
            }

            // Backend now sends arrays directly; fall back to JSON/comma-separated strings
            const safeArrayUpdate = (currentValue, newValue) => {
                if (Array.isArray(newValue)) {
                    return newValue;
                }
                if (typeof newValue === 'string' && newValue.trim()) {
                    try {
                        return JSON.parse(newValue);
                    } catch {
                        return newValue.split(',').map(item => item.trim()).filter(item => item);
                    }
                }
                return currentValue || [];
            };

            this.form.supplier_preference = safeArrayUpdate(this.form.supplier_preference, dataToUse.supplier_preference);

            if (! [null, undefined, ''].includes(dataToUse.event_management_services)) {
                this.form.event_management_service = Array.isArray(dataToUse.event_management_services)
                    ? dataToUse.event_management_services[0] : null;
            }
        }
    },
    mounted() {
        if (this.leadEvent) {
            this.updateFormFromLeadEvent(this.leadEvent);
        }
    },
    watch: {
        leadEvent: {
            handler(newLeadEvent) {
                if (newLeadEvent) {
                    this.updateFormFromLeadEvent(newLeadEvent);
                }
            },
            deep: true,
            immediate: true
        },
        sessionId: {
            handler() {
                this.updateFormFromLeadEvent(this.leadEvent);
            },
            immediate: false
        }
    }
};
</script>

<style lang="scss" scoped>
.services-tab {
    .el-checkbox {
        display: block;
        margin-bottom: 8px;
    }

    .el-radio {
        margin-right: 20px;
    }
}
</style>
